import type { ReactNode } from 'react';
import { cn } from './cn';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer. */
  cell: (row: T) => ReactNode;
  className?: string; // applied to both th + td
  align?: 'left' | 'right' | 'center';
}

interface DataTableProps<T> {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  /** Highlight a row (e.g. the newest item that just streamed in). */
  rowClassName?: (row: T) => string | undefined;
  empty?: ReactNode;
  /** Sticky header — for scrollable feeds. */
  stickyHeader?: boolean;
  className?: string;
}

const ALIGN: Record<NonNullable<Column<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

/** Lightweight, dependency-free table tuned for dense ops feeds. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  rowClassName,
  empty,
  stickyHeader,
  className,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <>{empty ?? <EmptyState title="Nothing here yet" />}</>;
  }
  return (
    <div className={cn('overflow-auto', className)}>
      <table className="w-full border-collapse text-[13px]">
        <thead className={cn(stickyHeader && 'sticky top-0 z-10')}>
          <tr className="bg-ink-850">
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn(
                  'border-b border-ink-700 px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted',
                  ALIGN[c.align ?? 'left'],
                  c.className,
                )}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className={cn(
                'border-b border-ink-800 transition-colors hover:bg-ink-850',
                rowClassName?.(row),
              )}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn('h-11 px-3.5 py-2.5 align-middle', ALIGN[c.align ?? 'left'], c.className)}
                >
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
